import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ShoppingCart, Save, X, ChevronDown, Package, FileText, Building2 } from 'lucide-react';
import ProductSelector from '../../components/sales/ProductSelector';
import CustomerAutocomplete from '../../components/sales/CustomerAutocomplete';
import ReferrerAutocomplete from '../../components/sales/ReferrerAutocomplete';
import PaymentMethodSelector from '../../components/sales/PaymentMethodSelector';
import BankChannelModal from '../../components/sales/BankChannelModal';
import { BranchSelector } from '../../components/branches';
import { createSale } from '../../api/sales';
import apiClient from '../../api/client';
import { toast } from 'react-toastify';
import { useAuth } from '../../contexts/AuthContext';
import { useBranch } from '../../contexts/BranchContext';

const SalesForm = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { currentBranchId, selectBranch, isMultiBranch, getCurrentBranch } = useBranch();
  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin' || user?.role === 'system_admin' || user?.role === 'retail_admin';
  // Users who can select multi-branch mode (draw stock from multiple branches)
  const canUseMultiBranchMode = ['super_admin', 'system_admin', 'retail_admin'].includes(user?.role);
  const [loading, setLoading] = useState(false);
  
  // Mobile accordion state
  const [activeSection, setActiveSection] = useState('products');

  // Sale data state
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [customer, setCustomer] = useState(null);
  const [referrer, setReferrer] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [paymentStatus, setPaymentStatus] = useState('paid');
  const [amountPaid, setAmountPaid] = useState(0);
  const [saleDiscount, setSaleDiscount] = useState(0);
  const [notes, setNotes] = useState('');
  
  // Multi-branch sale mode: enabled when "All Branches" is selected (currentBranchId === null)
  const isMultiBranchSale = canUseMultiBranchMode && currentBranchId === null;
  
  // Commission settings (for flexible commission types)
  const [commissionType, setCommissionType] = useState('percentage');
  const [commissionValue, setCommissionValue] = useState('');

  // Sync commission settings when referrer changes
  React.useEffect(() => {
    if (referrer) {
      setCommissionType(referrer.commission_type || 'percentage');
      // Prefer raw value if available (from new creation), otherwise check type
      if (referrer.raw_commission_value !== undefined) {
          setCommissionValue(referrer.raw_commission_value.toString());
      } else if (referrer.commission_type === 'fixed') {
          setCommissionValue(referrer.fixed_commission_amount?.toString() || '');
      } else {
          setCommissionValue(referrer.commission_rate?.toString() || '');
      }
    } else {
      setCommissionType('percentage');
      setCommissionValue('');
    }
  }, [referrer]);

  // Clear branch allocations when switching from multi-branch to single-branch mode
  React.useEffect(() => {
    if (!isMultiBranchSale && selectedProducts.length > 0) {
      // Check if any product has branch allocations that need clearing
      const hasAllocations = selectedProducts.some(p => p.branchAllocations?.length > 0);
      if (hasAllocations) {
        const cleanedProducts = selectedProducts.map(p => ({
          ...p,
          branchAllocations: null
        }));
        setSelectedProducts(cleanedProducts);
      }
    }
  }, [isMultiBranchSale]);

  // Bank channel modal state for bank/POS payments
  const [showBankChannelModal, setShowBankChannelModal] = useState(false);
  const [pendingSaleId, setPendingSaleId] = useState(null);
  const [pendingSaleAmount, setPendingSaleAmount] = useState(0);

  // Calculate totals
  const calculateTotals = () => {
    const subtotal = selectedProducts.reduce((sum, product) => {
      return sum + ((product.selling_price - product.unit_discount) * product.quantity);
    }, 0);

    const totalDiscount = selectedProducts.reduce((sum, product) => {
      return sum + (product.unit_discount * product.quantity);
    }, 0) + saleDiscount;

    const totalAmount = subtotal - saleDiscount;

    const totalCost = selectedProducts.reduce((sum, product) => {
      return sum + (product.cost_price * product.quantity);
    }, 0);

    const totalQuantity = selectedProducts.reduce((sum, product) => {
      return sum + product.quantity;
    }, 0);

    const grossProfit = totalAmount - totalCost;

    // Flexible commission calculation based on commission type
    let commissionAmount = 0;
    if (referrer && commissionValue) {
      const rate = parseFloat(commissionValue) || 0;
      switch (commissionType) {
        case 'percentage':
          commissionAmount = (grossProfit * rate) / 100;
          break;
        case 'per_unit':
          commissionAmount = totalQuantity * rate;
          break;
        case 'fixed':
          commissionAmount = rate;
          break;
        default:
          commissionAmount = (grossProfit * rate) / 100;
      }
      // Ensure commission is not negative
      commissionAmount = Math.max(0, commissionAmount);
    }

    const netProfit = grossProfit - commissionAmount;

    return {
      subtotal,
      totalDiscount,
      totalAmount,
      totalCost,
      totalQuantity,
      grossProfit,
      commissionAmount,
      netProfit
    };
  };

  const totals = calculateTotals();

  // Validate form
  const validateForm = () => {
    if (selectedProducts.length === 0) {
      toast.error('Please add at least one product to the sale');
      return false;
    }

    // Check all products have quantity > 0
    const zeroQuantityProducts = selectedProducts.filter(p => !p.quantity || p.quantity <= 0);
    if (zeroQuantityProducts.length > 0) {
      toast.error(`Please set quantity for: ${zeroQuantityProducts.map(p => p.product_code).join(', ')}`);
      return false;
    }

    // Multi-branch validation: ensure all products have proper branch allocations
    if (isMultiBranchSale) {
      for (const product of selectedProducts) {
        const allocations = (product.branchAllocations || []).filter(a => a.quantity > 0);
        if (allocations.length === 0) {
          toast.error(`Please allocate "${product.product_code}" to at least one branch`);
          return false;
        }
        const totalAllocated = allocations.reduce((sum, a) => sum + a.quantity, 0);
        if (totalAllocated !== product.quantity) {
          toast.error(`Branch allocations for "${product.product_code}" don't match total quantity`);
          return false;
        }
      }
    }

    if (!customer) {
      toast.error('Please select or add a customer');
      return false;
    }

    if (!paymentMethod) {
      toast.error('Please select a payment method');
      return false;
    }

    return true;
  };

  // Submit sale
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      const saleData = {
        customer: {
          customer_id: customer.customer_id,
          full_name: customer.full_name,
          phone: customer.phone,
          email: customer.email,
          address: customer.address,
          city: customer.city,
          state: customer.state
        },
        items: selectedProducts.map(product => {
          // Only include branch allocations if in multi-branch mode and allocations exist
          let allocations = null;
          if (isMultiBranchSale && product.branchAllocations?.length > 0) {
            // Filter to only include allocations with quantity > 0
            allocations = product.branchAllocations.filter(a => a.quantity > 0);
            if (allocations.length === 0) allocations = null;
          }
          return {
            product_id: product.product_id,
            quantity: product.quantity,
            selling_price: product.selling_price,
            unit_discount: product.unit_discount || 0,
            branchAllocations: allocations
          };
        }),
        payment_method: paymentMethod,
        payment_status: paymentStatus,
        amount_paid: amountPaid,
        discount: {
          type: 'fixed',
          value: saleDiscount
        },
        sale_notes: notes.trim() || null,
        branch_id: currentBranchId // Include branch context
      };

      // Add referrer if selected
      if (referrer) {
        saleData.referrer = {
          referrer_id: referrer.referrer_id,
          full_name: referrer.full_name,
          phone: referrer.phone,
          email: referrer.email,
          commission_rate: referrer.commission_rate
        };
        // Include commission type and value for flexible commission
        saleData.commission_type = commissionType;
        saleData.commission_rate_value = parseFloat(commissionValue) || 0;
      }

      const response = await createSale(saleData);
      const saleId = response.data.id;
      const receiptNumber = response.data.receipt_number;

      // Check if this is a bank/POS payment that needs channel selection
      const isBankPayment = ['transfer', 'pos', 'card', 'bank_transfer'].includes(paymentMethod.toLowerCase());
      
      if (isBankPayment) {
        // Show bank channel modal
        setPendingSaleId(saleId);
        setPendingSaleAmount(totals.totalAmount);
        setShowBankChannelModal(true);
        toast.success(`Sale created! Receipt: ${receiptNumber}`);
      } else {
        // Cash payment - navigate directly
        toast.success(`Sale created successfully! Receipt: ${receiptNumber}`);
        navigate(`/sales?showReceipt=${saleId}`);
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to create sale');
    } finally {
      setLoading(false);
    }
  };

  // Handle bank channel selection after sale
  const handleBankChannelSelect = async (channelData) => {
    try {
      await apiClient.put(`/sales/${pendingSaleId}/bank-channel`, {
        bank_channel_id: channelData.bank_channel_id
      });
      toast.success(`Bank channel set: ${channelData.channel_name}`);
      setShowBankChannelModal(false);
      navigate(`/sales?showReceipt=${pendingSaleId}`);
    } catch (error) {
      toast.error('Failed to set bank channel');
    }
  };

  // Skip bank channel selection
  const handleSkipBankChannel = () => {
    toast.info('You can add bank channel later from the sales list');
    setShowBankChannelModal(false);
    navigate(`/sales?showReceipt=${pendingSaleId}`);
  };

  const handleCancel = () => {
    if (selectedProducts.length > 0 || customer) {
      if (window.confirm('Are you sure you want to cancel? All data will be lost.')) {
        navigate('/sales');
      }
    } else {
      navigate('/sales');
    }
  };

  return (
    <div className="min-h-screen bg-secondary-offWhite p-3 sm:p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6 mb-4 sm:mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center gap-3">
              <ShoppingCart className="w-6 h-6 sm:w-8 sm:h-8 text-accent-gold flex-shrink-0" />
              <div>
                <h1 className="text-xl sm:text-2xl font-bold text-primary-black">New Sale</h1>
                <p className="text-xs sm:text-sm text-secondary-gray">Create a new sale transaction</p>
              </div>
            </div>
            
            {/* Branch Selector - shows only in multi-branch mode */}
            {isMultiBranch && (
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-500 hidden sm:inline">Branch:</span>
                <BranchSelector
                  selectedBranchId={currentBranchId}
                  onBranchChange={selectBranch}
                  showAllOption={canUseMultiBranchMode}
                  compact
                />
                {isMultiBranchSale && (
                  <span className="px-2 py-0.5 text-xs font-medium bg-blue-100 text-blue-700 rounded-full hidden sm:inline">
                    Multi-Branch
                  </span>
                )}
              </div>
            )}
            
            <div className="flex gap-2 w-full sm:w-auto">
              <button
                type="button"
                onClick={handleCancel}
                disabled={loading}
                className="flex-1 sm:flex-none flex items-center justify-center gap-2 px-3 sm:px-4 py-2 bg-white border border-secondary-lightGray rounded-lg text-secondary-gray hover:bg-secondary-offWhite transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm"
              >
                <X className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" />
                <span className="whitespace-nowrap">Cancel</span>
              </button>
              <button
                type="button"
                onClick={handleSubmit}
                disabled={loading || selectedProducts.length === 0}
                className="flex-1 sm:flex-none flex items-center justify-center gap-2 px-4 sm:px-6 py-2 bg-accent-gold text-white rounded-lg hover:bg-yellow-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm"
              >
                <Save className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" />
                <span className="whitespace-nowrap">{loading ? 'Saving...' : 'Complete Sale'}</span>
              </button>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
            {/* Left Column - Products */}
            <div className="lg:col-span-2 space-y-4 sm:space-y-6">
              {/* Mobile Accordion Header for Products */}
              <button
                type="button"
                onClick={() => setActiveSection(activeSection === 'products' ? 'details' : 'products')}
                className="lg:hidden w-full flex items-center justify-between p-4 bg-gradient-to-r from-accent-gold to-gray-800 text-white rounded-lg shadow-md"
              >
                <div className="flex items-center gap-2">
                  <Package className="w-5 h-5" />
                  <span className="font-bold">Products ({selectedProducts.length})</span>
                </div>
                <ChevronDown className={`w-5 h-5 transition-transform duration-300 ${activeSection === 'products' ? 'rotate-180' : ''}`} />
              </button>
              
              {/* Product Selection - Collapsible on mobile */}
              <div className={`bg-white rounded-lg shadow-sm p-4 sm:p-6 ${activeSection !== 'products' ? 'hidden lg:block' : ''}`}>
                <h2 className="hidden lg:block text-base sm:text-lg font-semibold text-primary-black mb-3 sm:mb-4">Products</h2>
                <ProductSelector
                  onProductsChange={setSelectedProducts}
                  selectedProducts={selectedProducts}
                  isMultiBranch={isMultiBranchSale}
                />
              </div>
            </div>

            {/* Right Column - Customer, Referrer, Payment */}
            <div className="space-y-4 sm:space-y-6">
              {/* Mobile Accordion Header for Order Details */}
              <button
                type="button"
                onClick={() => setActiveSection(activeSection === 'details' ? 'products' : 'details')}
                className="lg:hidden w-full flex items-center justify-between p-4 bg-gradient-to-r from-accent-gold to-gray-800 text-white rounded-lg shadow-md"
              >
                <div className="flex items-center gap-2">
                  <FileText className="w-5 h-5" />
                  <span className="font-bold">Order Details</span>
                  {customer && <span className="text-xs bg-green-500 px-2 py-0.5 rounded-full">✓</span>}
                </div>
                <ChevronDown className={`w-5 h-5 transition-transform duration-300 ${activeSection === 'details' ? 'rotate-180' : ''}`} />
              </button>
              
              {/* Order Details Content - Collapsible on mobile */}
              <div className={`space-y-4 sm:space-y-6 ${activeSection !== 'details' ? 'hidden lg:block' : ''}`}>
              {/* Customer */}
              <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6">
                <h2 className="text-base sm:text-lg font-semibold text-primary-black mb-3 sm:mb-4">
                  Customer <span className="text-red-600">*</span>
                </h2>
                <CustomerAutocomplete
                  onCustomerChange={setCustomer}
                  selectedCustomer={customer}
                />
              </div>

              {/* Referrer (Optional) */}
              <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6">
                <h2 className="text-base sm:text-lg font-semibold text-primary-black mb-3 sm:mb-4">
                  Referrer <span className="text-xs text-secondary-gray font-normal">(Optional)</span>
                </h2>
                <ReferrerAutocomplete
                  onReferrerChange={setReferrer}
                  selectedReferrer={referrer}
                  commissionType={commissionType}
                  commissionValue={commissionValue}
                  onCommissionTypeChange={setCommissionType}
                  onCommissionValueChange={setCommissionValue}
                />
              </div>

              {/* Sale Summary */}
              <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6">
                <h2 className="text-base sm:text-lg font-semibold text-primary-black mb-3 sm:mb-4">Sale Summary</h2>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-secondary-gray">Subtotal:</span>
                    <span className="font-medium text-primary-black">
                      ₦{totals.subtotal.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-secondary-gray">Product Discounts:</span>
                    <span className="font-medium text-red-600">
                      -₦{(totals.totalDiscount - saleDiscount).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                    </span>
                  </div>

                  {/* Additional Sale Discount */}
                  <div className="pt-2 border-t border-secondary-lightGray">
                    <label className="block text-xs text-secondary-gray mb-1">Additional Discount</label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-secondary-gray text-sm">₦</span>
                      <input
                        type="text"
                        inputMode="decimal"
                        value={saleDiscount}
                        onChange={(e) => {
                          const value = e.target.value.replace(/[^0-9.]/g, '');
                          setSaleDiscount(parseFloat(value) || 0);
                        }}
                        className="w-full pl-8 pr-4 py-2 bg-white border border-secondary-lightGray rounded-lg focus:outline-none focus:ring-2 focus:ring-accent-gold"
                        placeholder="0.00"
                      />
                    </div>
                  </div>

                  <div className="flex justify-between pt-2 border-t border-secondary-lightGray">
                    <span className="font-semibold text-primary-black">Total Amount:</span>
                    <span className="font-bold text-lg text-accent-gold">
                      ₦{totals.totalAmount.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                    </span>
                  </div>

                  {/* Profit Information - Admin Only */}
                  {isAdmin && (
                    <div className="pt-3 border-t border-secondary-lightGray space-y-1">
                      <div className="flex justify-between text-xs">
                        <span className="text-secondary-gray">Cost:</span>
                        <span className="text-secondary-gray">
                          ₦{totals.totalCost.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                        </span>
                      </div>
                      <div className="flex justify-between text-xs">
                        <span className="text-secondary-gray">{totals.grossProfit >= 0 ? 'Gross Profit:' : 'Gross Loss:'}</span>
                        <span className={`font-medium ${totals.grossProfit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                          ₦{totals.grossProfit.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                        </span>
                      </div>
                      {referrer && (
                        <div className="flex justify-between text-xs">
                          <span className="text-secondary-gray">Commission ({referrer.commission_rate}%):</span>
                          <span className="text-yellow-600">
                            -₦{totals.commissionAmount.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                          </span>
                        </div>
                      )}
                      <div className="flex justify-between text-xs font-semibold">
                        <span className="text-primary-black">{totals.netProfit >= 0 ? 'Net Profit:' : 'Net Loss:'}</span>
                        <span className={totals.netProfit >= 0 ? 'text-green-600' : 'text-red-600'}>
                          ₦{totals.netProfit.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Payment */}
              <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6">
                <h2 className="text-base sm:text-lg font-semibold text-primary-black mb-3 sm:mb-4">Payment</h2>
                <PaymentMethodSelector
                  paymentMethod={paymentMethod}
                  onPaymentMethodChange={setPaymentMethod}
                  paymentStatus={paymentStatus}
                  onPaymentStatusChange={setPaymentStatus}
                  amountPaid={amountPaid}
                  onAmountPaidChange={setAmountPaid}
                  totalAmount={totals.totalAmount}
                />
              </div>

              {/* Notes */}
              <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6">
                <h2 className="text-base sm:text-lg font-semibold text-primary-black mb-3 sm:mb-4">
                  Notes <span className="text-xs text-secondary-gray font-normal">(Optional)</span>
                </h2>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={4}
                  className="w-full px-3 py-2 border border-secondary-lightGray rounded-lg focus:outline-none focus:ring-2 focus:ring-accent-gold resize-none text-sm sm:text-base"
                  placeholder="Add any notes or special instructions for this sale..."
                />
              </div>
              </div> {/* End Order Details Content */}
            </div>
          </div>
        </form>
      </div>
      
      {/* Sticky Bottom Action Bar */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-secondary-lightGray shadow-lg z-40 p-3 sm:p-4 lg:pl-64">
        <div className="max-w-7xl mx-auto flex gap-3">
          <button
            type="button"
            onClick={handleCancel}
            disabled={loading}
            className="flex-1 sm:flex-none flex items-center justify-center gap-2 px-4 sm:px-6 py-3 bg-white border border-secondary-lightGray rounded-lg text-secondary-gray hover:bg-secondary-offWhite transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium"
          >
            <X className="w-5 h-5 flex-shrink-0" />
            <span>Cancel</span>
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={loading || selectedProducts.length === 0}
            className="flex-1 flex items-center justify-center gap-2 px-6 sm:px-8 py-3 bg-accent-gold text-white rounded-lg hover:bg-yellow-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-semibold"
          >
            <Save className="w-5 h-5 flex-shrink-0" />
            <span>{loading ? 'Creating Sale...' : 'Complete Sale'}</span>
          </button>
        </div>
      </div>
      
      {/* Bottom spacing to prevent content being hidden behind sticky bar */}
      <div className="h-20" />

      {/* Bank Channel Modal for Bank/POS payments */}
      <BankChannelModal
        isOpen={showBankChannelModal}
        onClose={() => setShowBankChannelModal(false)}
        onSelect={handleBankChannelSelect}
        onSkip={handleSkipBankChannel}
        saleAmount={pendingSaleAmount}
        paymentMethod={paymentMethod}
      />
    </div>
  );
};

export default SalesForm;
